import { matchPath } from 'umi';
import { getDatabrainSystemCode, getDatabrainSystemCodeFromPath, getUrlObject } from '@home/utils/urlConfig';
import { flattenUrlCollectionRoutes } from '@home/utils/utils';
import { EntityType, Permission, Status, URL_COLLECTION_ANALYSIS_FAILURE_REASON } from '@home/enums/urlCollection';

import type {
  IAnalysisUrlInfoHandler,
  IAnalysisUrlInfoParams,
  IAnalysisUrlInfoResult,
  IGetEntityDetailsByIdResult,
} from '@home/enums/urlCollection';
import type { TDBSystemCode } from '@home/types/aiSearch';

const BASE_PATH_REGEXP = /^\/(?:v2|rebuildv2)(?=\/)/i;
const DATABRAIN_BASE_PATHS = ['v2', 'rebuildv2'];

interface IUrlCollectionAnalysisContextOptions {
  systemCode?: string;
  failureReason?: IAnalysisUrlInfoResult['failure_reason'];
}

interface ICreateRouteOnlyAnalysisUrlInfoHandlerContext {
  url: string;
  pagePath: string;
  urlObj: URL | null;
  defaultResult: IAnalysisUrlInfoResult;
  baseSuccessResult: IAnalysisUrlInfoResult;
}

interface ICreateRouteOnlyAnalysisUrlInfoHandlerOptions {
  systemCode: string;
  routePaths?: string[];
  isRouteMatched?: (pathname: string) => boolean;
  getPageName?: (urlObj: URL | null) => string;
  getResult?: (context: ICreateRouteOnlyAnalysisUrlInfoHandlerContext) => Promise<IAnalysisUrlInfoResult | null>;
}

const formatWord = (word: string): string => {
  if (!word) {
    return '';
  }

  return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
};

const shouldUppercaseHyphenSegment = (segment: string): boolean => {
  const segmentParts = segment.split('-');

  return (
    segmentParts.length > 1 &&
    segmentParts[0].length === 1 &&
    segmentParts.every((item) => {
      return /^[a-z]+$/i.test(item);
    })
  );
};

const splitSegmentToWords = (segment: string): string[] => {
  if (!segment) {
    return [];
  }

  if (shouldUppercaseHyphenSegment(segment)) {
    return [segment.toUpperCase()];
  }

  return segment
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => formatWord(word));
};

export const getUrlCollectionPathSegments = (urlObj?: URL | null): string[] => {
  if (!urlObj) {
    return [];
  }

  return urlObj.pathname.split('/').filter(Boolean);
};

export const getUrlCollectionSystemPathSegments = (pathSegments: string[]): string[] => {
  if (!pathSegments.length) {
    return [];
  }

  const firstSegment = pathSegments[0]?.toLowerCase() || '';

  if (DATABRAIN_BASE_PATHS.includes(firstSegment)) {
    return pathSegments.slice(2);
  }

  return pathSegments.slice(1);
};

export const getUrlCollectionNormalizedPathname = (pathname = ''): string => {
  const normalizedPathname = pathname.replace(BASE_PATH_REGEXP, '');

  return normalizedPathname || '/';
};

export const getUrlCollectionPagePath = (urlObj?: URL | null): string => {
  if (!urlObj) {
    return '';
  }

  return `${urlObj.pathname}${urlObj.search}`;
};

export const formatUrlCollectionPageName = (pathSegments: string[]): string => {
  if (!pathSegments.length) {
    return '';
  }

  return pathSegments.flatMap((segment) => splitSegmentToWords(segment)).join(' ');
};

export const getUrlCollectionBusinessPageNameByUrlObject = (urlObj?: URL | null): string => {
  const pathSegments = getUrlCollectionPathSegments(urlObj);
  const businessPathSegments = getUrlCollectionSystemPathSegments(pathSegments);

  if (businessPathSegments.length) {
    return formatUrlCollectionPageName(businessPathSegments);
  }

  if (!pathSegments.length) {
    return '';
  }

  const firstSegment = pathSegments[0]?.toLowerCase() || '';
  const systemPathSegments = DATABRAIN_BASE_PATHS.includes(firstSegment)
    ? pathSegments.slice(1, 2)
    : pathSegments.slice(0, 1);

  return formatUrlCollectionPageName(systemPathSegments);
};

export const getDefaultUrlCollectionAnalysisUrlInfoResult = (
  url: string,
  urlObj?: URL | null,
  options?: {
    permission?: Permission;
    status?: Status;
    failureReason?: IAnalysisUrlInfoResult['failure_reason'];
    system?: string;
    pagePath?: string;
  },
): IAnalysisUrlInfoResult => {
  const currentUrlObj = urlObj === undefined ? getUrlObject(url) : urlObj;
  const pagePath = options?.pagePath ?? getUrlCollectionPagePath(currentUrlObj);
  const system = Object.prototype.hasOwnProperty.call(options || {}, 'system')
    ? ((options?.system || '') as TDBSystemCode)
    : getDatabrainSystemCodeFromPath(pagePath) || getDatabrainSystemCode(url);

  return {
    success: false,
    failure_reason: options?.failureReason,
    system,
    permission: options?.permission || Permission.HasPermission,
    status: options?.status || Status.Unavailable,
    entity_type: [],
    entity_id: '',
    entity_name: '',
    entity_cover: '',
    page_url: url || '',
    page_path: pagePath,
    page_name: '',
  };
};

export const getBaseUrlCollectionSuccessResult = (
  url: string,
  urlObj?: URL | null,
  options?: {
    pageName?: string;
    permission?: Permission;
    status?: Status;
    system?: string;
    pagePath?: string;
  },
): IAnalysisUrlInfoResult => {
  const currentUrlObj = urlObj === undefined ? getUrlObject(url) : urlObj;

  return {
    ...getDefaultUrlCollectionAnalysisUrlInfoResult(url, currentUrlObj, {
      system: options?.system,
      pagePath: options?.pagePath,
    }),
    success: true,
    failure_reason: undefined,
    permission: options?.permission || Permission.HasPermission,
    status: options?.status || Status.Available,
    page_name: options?.pageName || getUrlCollectionBusinessPageNameByUrlObject(currentUrlObj),
  };
};

export const getUrlCollectionAnalysisContext = (
  params?: IAnalysisUrlInfoParams,
  options?: IUrlCollectionAnalysisContextOptions,
): {
  url: string;
  pagePath: string;
  urlObj: URL | null;
  systemCode: string;
  defaultResult: IAnalysisUrlInfoResult;
} => {
  const url = params?.url || '';
  const pagePath = params?.page_path || '';
  const urlObj = getUrlObject(pagePath);
  const systemCode = options?.systemCode || getDatabrainSystemCodeFromPath(pagePath) || getDatabrainSystemCode(url);

  return {
    url,
    pagePath,
    urlObj,
    systemCode,
    defaultResult: getDefaultUrlCollectionAnalysisUrlInfoResult(url, urlObj, {
      failureReason: options?.failureReason || URL_COLLECTION_ANALYSIS_FAILURE_REASON.UnsupportedUrl,
      system: systemCode,
      pagePath,
    }),
  };
};

export const getEmptyUrlCollectionEntityDetailsResult = (): IGetEntityDetailsByIdResult => {
  return {
    entity_type: [],
    entity_id: '',
    entity_name: '',
    entity_cover: '',
  };
};

export const getUrlCollectionEntityDetailsResult = (params?: {
  entityType?: EntityType | EntityType[] | '';
  entityId?: string;
  entityName?: string;
  entityCover?: string;
}): IGetEntityDetailsByIdResult => {
  const entityType = params?.entityType || '';
  let entityTypes: EntityType[] = [];

  if (Array.isArray(entityType)) {
    entityTypes = entityType;
  } else if (entityType) {
    entityTypes = [entityType];
  }

  return {
    entity_type: entityTypes,
    entity_id: params?.entityId || '',
    entity_name: params?.entityName || '',
    entity_cover: params?.entityCover || '',
  };
};

export const getUrlCollectionEntityPageName = (pageName = '', entityName = ''): string => {
  return entityName || pageName;
};

export const isUrlCollectionRouteMatched = (pathname: string, routePaths: string[]): boolean => {
  const normalizedPathname = getUrlCollectionNormalizedPathname(pathname);

  return routePaths.some((path) => {
    return matchPath(normalizedPathname, { path, exact: true });
  });
};

export const parseUrlCollectionJsonParam = (value = ''): Record<string, any> => {
  if (!value) {
    return {};
  }

  try {
    return JSON.parse(decodeURIComponent(value));
  } catch (error) {
    try {
      return JSON.parse(value);
    } catch (nextError) {
      return {};
    }
  }
};

export const getUrlCollectionEntityTypes = (value: unknown): EntityType[] => {
  const rawValues = Array.isArray(value) ? value : [value];
  const normalizedValues = rawValues
    .flatMap((item) => String(item || '').split(','))
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const uniquePlatformTypes = Array.from(
    new Set(
      normalizedValues.filter((item) => {
        return [EntityType.Mobile, EntityType.PC, EntityType.Console].includes(item as EntityType);
      }),
    ),
  );

  if (normalizedValues.includes(EntityType.Company)) {
    return [EntityType.Company];
  }

  if (
    normalizedValues.includes(EntityType.Combine) ||
    (uniquePlatformTypes.includes(EntityType.PC) && uniquePlatformTypes.includes(EntityType.Console))
  ) {
    return [EntityType.Combine];
  }

  if (uniquePlatformTypes.length === 1) {
    return [uniquePlatformTypes[0] as EntityType];
  }

  return [];
};

/**
 * 创建基于 pathname + search 的 URL 解析器。
 *
 * 总入口负责完成原始 URL 校验、域名白名单判断和 page_path 归一；子系统只需要处理
 * 路由匹配与业务实体解析。
 */
export const createRouteOnlyAnalysisUrlInfoHandler = (
  options: ICreateRouteOnlyAnalysisUrlInfoHandlerOptions,
): IAnalysisUrlInfoHandler => {
  return async (params: IAnalysisUrlInfoParams): Promise<IAnalysisUrlInfoResult> => {
    const { url, pagePath, urlObj, defaultResult } = getUrlCollectionAnalysisContext(params, {
      systemCode: options.systemCode,
    });

    const matched =
      typeof options.isRouteMatched === 'function'
        ? options.isRouteMatched(urlObj?.pathname || '')
        : isUrlCollectionRouteMatched(urlObj?.pathname || '', options.routePaths || []);

    if (!matched) {
      return defaultResult;
    }

    const baseSuccessResult = getBaseUrlCollectionSuccessResult(url, urlObj, {
      pageName: options.getPageName?.(urlObj),
      system: options.systemCode,
      pagePath,
    });

    if (typeof options.getResult !== 'function') {
      return baseSuccessResult;
    }

    let result = null;

    try {
      result = await options.getResult({
        url,
        pagePath,
        urlObj,
        defaultResult,
        baseSuccessResult,
      });
    } catch (error) {
      result = null;
    }

    return result || baseSuccessResult;
  };
};

export { flattenUrlCollectionRoutes };
